import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Search, ShieldCheck, MapPin, TrendingUp, Heart, Eye, Building2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function BuyerDashboard() {
  const { API } = useAuth();
  const [listings, setListings] = useState([]);
  const [interests, setInterests] = useState([]);
  const [search, setSearch] = useState("");
  const [showInterest, setShowInterest] = useState(false);
  const [selectedProp, setSelectedProp] = useState(null);
  const [message, setMessage] = useState("");
  const [activeTab, setActiveTab] = useState("marketplace");

  const fetchData = useCallback(async () => {
    try {
      const [lRes, iRes] = await Promise.all([
        fetch(`${API}/marketplace/listings`, { credentials: "include" }),
        fetch(`${API}/marketplace/my-interests`, { credentials: "include" }),
      ]);
      if (lRes.ok) setListings(await lRes.json());
      if (iRes.ok) setInterests(await iRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const expressInterest = async () => {
    if (!selectedProp) return;
    try {
      await fetch(`${API}/marketplace/interest`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ property_id: selectedProp.property_id, message }),
      });
      setShowInterest(false);
      setSelectedProp(null);
      setMessage("");
      fetchData();
    } catch (e) { console.error(e); }
  };

  const filtered = listings.filter(l =>
    l.name?.toLowerCase().includes(search.toLowerCase()) ||
    l.address?.toLowerCase().includes(search.toLowerCase()) ||
    l.village?.toLowerCase().includes(search.toLowerCase())
  );

  const getRiskColor = (score) => score == null ? "#71717A" : score <= 30 ? "#556B2F" : score <= 60 ? "#F59E0B" : "#ef4444";

  return (
    <div data-testid="buyer-dashboard">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Property Marketplace</h2>
          <p className="text-white/40 text-sm font-sans">Discover verified, risk-scored properties</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 bg-[#11130F] border border-white/10 rounded-sm p-1 w-fit">
        {[{ key: "marketplace", label: "Browse Properties" }, { key: "interests", label: "My Interests" }].map(t => (
          <button key={t.key} data-testid={`buyer-tab-${t.key}`} onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-xs font-sans rounded-sm transition-colors ${activeTab === t.key ? "bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/20" : "text-white/50 hover:text-white"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === "marketplace" && (
        <>
          <div className="flex gap-3 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input data-testid="buyer-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, location, village..." className="pl-10 bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <Building2 className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No verified listings available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(prop => {
                const riskScore = prop.risk_score;
                const riskColor = getRiskColor(riskScore);
                const badges = prop.badges || [];
                return (
                  <div key={prop.property_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
                    <div className="flex items-start justify-between mb-3">
                      <h4 className="font-serif text-base text-white">{prop.name}</h4>
                      {riskScore != null && (
                        <span className="text-[10px] font-sans font-medium px-2 py-0.5 rounded-sm" style={{ color: riskColor, backgroundColor: riskColor + "15" }}>
                          Risk: {riskScore}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1 text-white/40 text-xs font-sans mb-2">
                      <MapPin className="w-3 h-3" /> {prop.address}
                    </div>
                    {prop.survey_number && (
                      <p className="text-white/30 text-[10px] font-sans mb-2">Survey: {prop.survey_number}{prop.sub_division ? `/${prop.sub_division}` : ""} — {prop.village}</p>
                    )}
                    {/* Badges */}
                    <div className="flex flex-wrap gap-1 mb-3">
                      {badges.slice(0, 3).map(b => (
                        <span key={b} className="text-[9px] px-1.5 py-0.5 rounded-sm bg-[#D4AF37]/10 text-[#D4AF37] font-sans flex items-center gap-0.5">
                          <ShieldCheck className="w-2.5 h-2.5" /> {b.replace(/_/g, " ")}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-xs font-sans mb-3">
                      <span className="text-white/40">Valuation</span>
                      <span className="text-white font-medium">${prop.valuation?.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-sans mb-4">
                      <span className="text-white/40">Area</span>
                      <span className="text-white">{prop.area_sqft?.toLocaleString()} sqft</span>
                    </div>
                    <Button data-testid={`interest-${prop.property_id}`} onClick={() => { setSelectedProp(prop); setShowInterest(true); }}
                      className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans text-xs">
                      <Heart className="w-3 h-3 mr-1" /> Express Interest
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {activeTab === "interests" && (
        <div className="space-y-3">
          {interests.length === 0 ? (
            <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
              <Heart className="w-12 h-12 text-white/10 mx-auto mb-4" />
              <p className="text-white/40 text-sm font-sans">No interests expressed yet. Browse the marketplace to find properties.</p>
            </div>
          ) : interests.map(i => (
            <div key={i.interest_id} className="bg-[#11130F] border border-white/10 rounded-sm p-4 hover:border-[#D4AF37]/40 transition-colors">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-sans font-medium text-white text-sm">{i.property?.name || i.property_id}</h4>
                  <p className="text-white/40 text-xs font-sans">{i.property?.address}</p>
                  <p className="text-white/30 text-[10px] font-sans mt-1">Message: {i.message}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-sm font-sans ${i.status === "pending" ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "bg-[#556B2F]/10 text-[#556B2F]"}`}>
                  {i.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showInterest} onOpenChange={setShowInterest}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-xl">Express Interest</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selectedProp && (
              <div className="bg-white/5 border border-white/10 rounded-sm p-3">
                <p className="text-white font-sans text-sm">{selectedProp.name}</p>
                <p className="text-white/40 text-xs font-sans">{selectedProp.address}</p>
              </div>
            )}
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Message to Owner</label>
              <Input data-testid="interest-message" value={message} onChange={e => setMessage(e.target.value)} placeholder="I'm interested in this property..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-interest-btn" onClick={expressInterest} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm">Submit Interest</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
